<template>
	<b-form @submit.stop.prevent="save($event); return false">
		<b-form-row>
			<b-col cols="12" lg="6">
				<b-card title="Options" class="mb-3">
					<b-form-row>
						<b-col>
							<b-form-checkbox switch inline v-model="settings.enableTTS">Enable text-to-speech</b-form-checkbox>
						</b-col>
						<b-col>
							<b-form-checkbox switch inline v-model="settings.enableEventSounds">Enable event sounds</b-form-checkbox>
						</b-col>
					</b-form-row>
				</b-card>
				<b-card title="Animation" class="mb-3">
					<b-form-row>
						<b-col>
							<b-form-group label="In Transition">
								<b-form-select v-model="settings.inTransition" :options="inTransitions"></b-form-select>
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Out Transition">
								<b-form-select v-model="settings.outTransition" :options="outTransitions"></b-form-select>
							</b-form-group>
						</b-col>
					</b-form-row>
					<b-form-row>
						<b-col>
							<b-form-group label="Animation duration" append="ms">
								<b-input-group append="ms">
									<b-form-input type="number" min="0" max="5000" v-model="settings.duration"></b-form-input>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Linger duration">
								<b-input-group append="ms">
									<b-form-input type="number" min="0" max="60000" v-model="settings.linger"></b-form-input>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Load timeout">
								<b-input-group append="ms">
									<b-form-input type="number" min="0" max="100000" step="1" v-model="settings.loadTimeout"></b-form-input>
								</b-input-group>
							</b-form-group>
						</b-col>
					</b-form-row>
				</b-card>
				<text-settings :settings="settings" />
				<b-card title="Colors" class="mb-3">
					<b-form-row>
						<b-col>
							<b-form-group label="Text Color">
								<color-picker v-model="settings.textColor" />
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Message Background">
								<color-picker v-model="settings.textBg" />
							</b-form-group>
						</b-col>
					</b-form-row>
					<b-form-row>
						<b-col>
							<b-form-group label="Gift Text Color">
								<color-picker v-model="settings.giftColor" />
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Gift Background">
								<color-picker v-model="settings.giftBg" />
							</b-form-group>
						</b-col>
					</b-form-row>
				</b-card>
				<b-card title="Outline" class="mb-3">
					<b-form-row>
						<b-col>
							<b-form-group label="Text outline blur">
								<b-input-group append="px">
									<b-form-input type="number" min="0" max="100" step="any" v-model="settings.blurRadius"></b-form-input>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Text outline size">
								<b-input-group append="px">
									<b-form-input type="number" min="0" max="10" step="any" v-model="settings.spreadRadius"></b-form-input>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col cols="6">
							<b-form-group label="Text Outline Color">
								<color-picker v-model="settings.shadowColor" />
							</b-form-group>
						</b-col>
					</b-form-row>
				</b-card>
				<b-card title="Padding" class="mb-3">
					<b-form-row>
						<b-col>
							<b-form-group label="Left" append="px">
								<b-input-group append="px">
									<b-input type="number" min="0" max="2000" v-model="settings.paddingLeft"/>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Bottom" append="px">
								<b-input-group append="px">
									<b-input type="number" min="0" max="2000" v-model="settings.paddingBottom"/>
								</b-input-group>
							</b-form-group>
						</b-col>
						<b-col>
							<b-form-group label="Right" append="px">
								<b-input-group append="px">
									<b-input type="number" min="0" max="2000" v-model="settings.paddingRight"/>
								</b-input-group>
							</b-form-group>
						</b-col>
					</b-form-row>
				</b-card>
				<b-card class="mb-3">
					<b-form-group label="Ignore chatters" description="One chatter per line">
						<b-textarea v-model="settings.ignoreUsers"></b-textarea>
					</b-form-group>
				</b-card>
				<b-card class="mb-3">
					<b-form-group label="Extra CSS">
						<b-textarea v-model="settings.extraCSS"></b-textarea>
					</b-form-group>
				</b-card>
			</b-col>
			<b-col cols="12" lg="6">
				<preview class="mb-3" slug="chat" name="Chat" />
			</b-col>
		</b-form-row>
		<b-card class="text-center mb-3">
			<b-alert variant="danger" :show="!!saveError">{{saveError}}</b-alert>
			<b-overlay :show="saving > 0" variant="dark" class="d-inline-block">
				<b-button variant="primary" type="submit">Save settings</b-button>
			</b-overlay>
		</b-card>
	</b-form>
</template>

<script>
import animateCssTransitions from '@/animateCssTransitions';
import ColorPicker from '@/components/ColorPicker.vue';
import Preview from '@/components/Widget/Preview.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';

export default {
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		settings: {},
		inTransitions: [ ...[{ text: 'Don\'t fade in', value: null }], ...animateCssTransitions.inTransitions ],
		outTransitions: [ ...[{ text: 'Don\'t fade out (log mode)', value: null }], ...animateCssTransitions.outTransitions ],
		curColor: {},
	}; },
	components: {
		ColorPicker,
		Preview,
		TextSettings,
	},
	async mounted() {
		try {
			this.saving = 0;
			this.saveError = null
			this.loading++;
			this.error = null;
			const settings = await this.$api.get('widgetSettings/chat');
			this.settings = {
				...settings,
				enableTTS: settings.enableTTS||false,
				enableEventSounds: settings.enableEventSounds||false,
				inTransition: settings.inTransition||null,
				outTransition: settings.outTransition||null,
				textColor: settings.textColor || 'rgba(255, 255, 255, 1)',
				shadowColor: settings.shadowColor || 'rgba(0, 0, 0, 1)',
				textBg: settings.textBg || 'rgba(0, 128, 0, 1)',
				giftColor: settings.giftColor || 'rgba(0, 0, 0, 1)',
				giftBg: settings.giftBg || 'rgba(255, 195, 0, 1)',
				fontFamily: settings.fontFamily || '',
				fontSize: settings.fontSize || 0,
				blurRadius: settings.blurRadius || 0,
				lineHeight: settings.lineHeight || 0,
				letterSpacing: settings.letterSpacing || 0,
				textAlign: settings.textAlign || 'left',
				boldText: settings.boldText || false,
				italicText: settings.italicText || false,
				underlineText: settings.underlineText || false,
				strikethroughText: settings.strikethroughText || false,
				uppercaseText: settings.uppercaseText || false,
				ignoreUsers: settings.ignoreUsers ? settings.ignoreUsers.join('\n') : '',
				extraCSS: settings.extraCSS || '',
				customFont: settings.customFont || false,
				paddingLeft: settings.paddingLeft || 0,
				paddingBottom: settings.paddingBottom || 0,
				paddingRight: settings.paddingRight || 0,
			};
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	methods: {
		async save() {
			try {
				this.saving++;
				this.error = null;

				await this.$api.post('widgetSettings/chat', {
					...this.settings,
					ignoreUsers: (this.settings.ignoreUsers||'').split('\n').map(a => a.replace(/^\s*(.*?)\s*$/, '$1')).filter(a => !!a)
				});
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	}
};
</script>
